import useStore, { DEFAULT_AGENT_MODEL_ID } from 'state/knovStore'
import { type AgentModelConfig, type AgentModelDefinition } from 'state/agentModelConfig'

interface DynamicAgentModelCandidate {
    id?: string | null
    name?: string | null
    humanName?: string | null
    actionTagName?: string | null
    provider?: string | null
    openclawIntegrationId?: string | null
    openclawOwnerUsername?: string | null
    openclawAlias?: string | null
}

interface AgentModelMentionDetails {
    id: string
    mentionHandle: string
    link: string
}

export function getAgentModelConfig(
    agentModelConfig?: AgentModelConfig | null,
): AgentModelConfig | null {
    return agentModelConfig || useStore.getState().agentModelConfig
}

export function getConfiguredAgentModels(
    agentModelConfig?: AgentModelConfig | null,
): AgentModelDefinition[] {
    const resolvedAgentModelConfig = getAgentModelConfig(agentModelConfig)
    const configuredModels = Array.isArray(resolvedAgentModelConfig?.models)
        ? resolvedAgentModelConfig.models
        : []
    const supportedModels = resolvedAgentModelConfig?.supportedModels || []

    const validConfiguredModels = configuredModels
        .filter(model => {
            return (
                typeof model?.id === 'string' &&
                model.id.trim().length > 0 &&
                supportedModels.includes(model.id)
            )
        })
        .map(model => ({
            id: model.id,
            label: model.label,
            mentionHandle: model.mentionHandle,
            group: model.group || 'other',
            default: Boolean(model.default),
            aliases: Array.isArray(model.aliases) ? model.aliases : [],
        }))

    if (validConfiguredModels.length > 0) {
        return validConfiguredModels
    }

    return []
}

export function resolveCanonicalAgentModelId(
    agentModel: string | null | undefined,
    agentModelConfig?: AgentModelConfig | null,
) {
    const candidateAgentModel = String(agentModel || '').trim()
    if (!candidateAgentModel) return null

    const resolvedAgentModelConfig = getAgentModelConfig(agentModelConfig)
    if (!resolvedAgentModelConfig) {
        return candidateAgentModel
    }

    const configuredAgentModels = getConfiguredAgentModels(resolvedAgentModelConfig)
    const matchedAgentModel = configuredAgentModels.find(model => model.id === candidateAgentModel)
    if (matchedAgentModel) {
        return matchedAgentModel.id
    }

    const configuredAgentModelAliases = resolvedAgentModelConfig.aliases || {}
    const aliasedModelId = configuredAgentModelAliases[candidateAgentModel]
    if (!aliasedModelId) {
        return null
    }

    const matchedAliasedModel = configuredAgentModels.find(model => model.id === aliasedModelId)
    return matchedAliasedModel?.id || null
}

function normalizeAgentModelCandidate(value: string | null | undefined): string {
    return String(value || '')
        .trim()
        .toLowerCase()
}

function isUuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
}

function isOpenclawModel(model: DynamicAgentModelCandidate): boolean {
    const provider = normalizeAgentModelCandidate(model.provider)
    const name = normalizeAgentModelCandidate(model.name)
    const actionTagName = normalizeAgentModelCandidate(model.actionTagName)

    return (
        provider === 'openclaw' ||
        name.startsWith('openclaw-') ||
        actionTagName.startsWith('openclaw') ||
        Boolean(model.openclawIntegrationId)
    )
}

function getOpenclawNamespacedAlias(model: DynamicAgentModelCandidate): string | null {
    return model.openclawOwnerUsername && model.openclawAlias
        ? `${model.openclawOwnerUsername}/${model.openclawAlias}`
        : null
}

function getOpenclawModelMatchValues(
    model: DynamicAgentModelCandidate,
): Array<string | null | undefined> {
    return [
        model.id,
        model.openclawIntegrationId,
        model.actionTagName,
        model.humanName,
        model.name,
        model.openclawAlias,
        getOpenclawNamespacedAlias(model),
    ]
}

function findOpenclawModelCandidate(
    agentModel: string,
    aiModels: DynamicAgentModelCandidate[],
): DynamicAgentModelCandidate | undefined {
    const normalizedCandidate = normalizeAgentModelCandidate(agentModel)

    return aiModels.find(model => {
        if (!isOpenclawModel(model)) return false

        return getOpenclawModelMatchValues(model).some(
            value => normalizeAgentModelCandidate(value) === normalizedCandidate,
        )
    })
}

export function resolveAgentModelSelectionId(
    agentModel: string | null | undefined,
    agentModelConfig?: AgentModelConfig | null,
    aiModels: DynamicAgentModelCandidate[] = [],
) {
    const canonicalAgentModel = resolveCanonicalAgentModelId(agentModel, agentModelConfig)
    if (canonicalAgentModel) return canonicalAgentModel

    const candidateAgentModel = String(agentModel || '').trim()
    if (!candidateAgentModel) return null

    // OpenClaw integration ids are dynamic UUIDs, not part of the static
    // agent-model config. Preserve them so selected @@ mentions are not
    // interpreted as a cleared model while aiModels are still loading.
    if (isUuid(candidateAgentModel)) return candidateAgentModel

    const matchedOpenclawModel = findOpenclawModelCandidate(candidateAgentModel, aiModels)

    if (!matchedOpenclawModel) return null

    return String(
        matchedOpenclawModel.id ||
            matchedOpenclawModel.openclawIntegrationId ||
            candidateAgentModel,
    )
}

export function getAgentModelMentionDetails(
    agentModel: string | null | undefined,
    agentModelConfig?: AgentModelConfig | null,
    aiModels: DynamicAgentModelCandidate[] = [],
): AgentModelMentionDetails | null {
    const candidateAgentModel = String(agentModel || '').trim()
    if (!candidateAgentModel) return null

    const configuredModel = getAgentModelDefinition(candidateAgentModel, agentModelConfig)
    if (configuredModel) {
        return {
            id: configuredModel.id,
            mentionHandle: configuredModel.mentionHandle,
            link: `/models/${configuredModel.id}`,
        }
    }

    const matchedOpenclawModel = findOpenclawModelCandidate(candidateAgentModel, aiModels)
    if (matchedOpenclawModel) {
        const mentionId = String(
            matchedOpenclawModel.id ||
                matchedOpenclawModel.openclawIntegrationId ||
                candidateAgentModel,
        )
        const mentionHandle = String(
            matchedOpenclawModel.humanName ||
                matchedOpenclawModel.actionTagName ||
                getOpenclawNamespacedAlias(matchedOpenclawModel) ||
                matchedOpenclawModel.openclawAlias ||
                matchedOpenclawModel.name ||
                mentionId,
        )

        return {
            id: mentionId,
            mentionHandle,
            link: `/models/${mentionId}`,
        }
    }

    if (isUuid(candidateAgentModel)) return null

    return {
        id: candidateAgentModel,
        mentionHandle: candidateAgentModel,
        link: `/models/${candidateAgentModel}`,
    }
}

export function getDefaultAgentModel(agentModelConfig?: AgentModelConfig | null) {
    const resolvedAgentModelConfig = getAgentModelConfig(agentModelConfig)
    const configuredAgentModels = getConfiguredAgentModels(resolvedAgentModelConfig)
    const defaultModel =
        configuredAgentModels.find(model => model.default)?.id ||
        resolveCanonicalAgentModelId(
            resolvedAgentModelConfig?.defaultModel,
            resolvedAgentModelConfig,
        ) ||
        configuredAgentModels[0]?.id

    return defaultModel || DEFAULT_AGENT_MODEL_ID
}

export function getSupportedLlmModels(agentModelConfig?: AgentModelConfig | null) {
    return getConfiguredAgentModels(agentModelConfig).map(model => model.id)
}

export function getAgentModelDefinition(
    agentModel: string | null | undefined,
    agentModelConfig?: AgentModelConfig | null,
) {
    const resolvedAgentModelId = resolveCanonicalAgentModelId(agentModel, agentModelConfig)
    if (!resolvedAgentModelId) return null

    const configuredModels = getConfiguredAgentModels(agentModelConfig)
    return configuredModels.find(model => model.id === resolvedAgentModelId) || null
}

export function getAgentModelDisplayLabel(
    agentModel: string | null | undefined,
    agentModelConfig?: AgentModelConfig | null,
) {
    return getAgentModelDefinition(agentModel, agentModelConfig)?.label || ''
}

export function getAgentModelMentionLabel(
    agentModel: string | null | undefined,
    agentModelConfig?: AgentModelConfig | null,
) {
    return getAgentModelDefinition(agentModel, agentModelConfig)?.mentionHandle || ''
}
